import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { FETCH_ALLOCATION_INSTRUCTIONS } from '../../../api/queries/allocation';
import { stagingOptions } from '../../../constants/apollo';

const Instructions = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(FETCH_ALLOCATION_INSTRUCTIONS, stagingOptions);
  const instructions = data?.fetchAllocationInstructions;

  if (loading) {
    return (
      <div className="flex flex-col space-y-2">
        <div className="main-wrapper">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primaryColor border-r-transparent"></div>
              <p className="mt-4 text-gray-600">Loading instructions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !instructions) {
    return (
      <div className="flex flex-col space-y-2">
        <div className="main-wrapper">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold">Unable to load instructions</p>
              <p className="text-red-500 text-sm mt-2">
                {error?.message || 'Please try refreshing the page.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { overview, operators, commonParameters, examples } = instructions;

  return (
    <div className="flex flex-col space-y-2 bg-white pb-12">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/allocation');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-8">
            <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />

              <div className="relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 6.25278V19.2528M12 6.25278C10.8321 5.47686 9.24649 5 7.5 5C5.75351 5 4.16789 5.47686 3 6.25278V19.2528C4.16789 18.4769 5.75351 18 7.5 18C9.24649 18 10.8321 18.4769 12 19.2528M12 6.25278C13.1679 5.47686 14.7535 5 16.5 5C18.2465 5 19.8321 5.47686 21 6.25278V19.2528C19.8321 18.4769 18.2465 18 16.5 18C14.7535 18 13.1679 18.4769 12 19.2528" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-3xl font-bold text-white">{overview?.title || 'Allocation Rules System'}</h1>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                        Guide
                      </span>
                    </div>
                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                      {overview?.description || 'Control how orders are automatically allocated to partners, couriers, or internal processing.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">

            <Card className="rounded-xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 mb-6">
              <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center shadow-md">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">Overview</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                {overview?.sections && overview.sections.length > 0 ? (
                  overview.sections.map((section: any, index: number) => (
                    <div key={section.heading || index} className="space-y-3">
                      <h4 className="font-bold text-gray-900 flex items-center gap-2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {section.heading || `Section ${index + 1}`}
                      </h4>
                      <p className="text-gray-700 leading-relaxed">{section.content}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No overview sections available</p>
                )}

                <div>
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Rule Components
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="font-semibold text-gray-900 mb-1">Name</div>
                      <div className="text-sm text-gray-600">Friendly label for the rule.</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="font-semibold text-gray-900 mb-1">Priority</div>
                      <div className="text-sm text-gray-600">Lower number = higher priority (evaluated first).</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="font-semibold text-gray-900 mb-1">Result Via</div>
                      <div className="text-sm text-gray-600">Partner, Internal, Courier, or InternalWarehouse.</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="font-semibold text-gray-900 mb-1">Result Data</div>
                      <div className="text-sm text-gray-600">Specific partner, method, or courier ID.</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 md:col-span-2">
                      <div className="font-semibold text-gray-900 mb-1">Conditions</div>
                      <div className="text-sm text-gray-600">All conditions must match (logical AND).</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 mb-6">
              <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-warningColor to-orange-600 flex items-center justify-center shadow-md">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">Supported Operators</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {operators && operators.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Operator</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Label</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Description</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Example Value</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Cast Types</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {operators.map((operator: any) => (
                          <tr key={operator.operator} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-4 whitespace-nowrap">
                              <Badge variant="default" className="font-mono font-semibold">
                                {operator.operator}
                              </Badge>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-700 font-medium">{operator.label || '—'}</td>
                            <td className="px-5 py-4 text-sm text-gray-700">{operator.description || '—'}</td>
                            <td className="px-5 py-4 text-sm text-gray-600 font-mono bg-gray-50 rounded px-3 py-1.5">
                              {operator.exampleValue || '—'}
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex flex-wrap gap-1">
                                {operator.castTypes && operator.castTypes.length > 0 ? (
                                  operator.castTypes.map((castType: string) => (
                                    <Badge key={castType} variant="secondary" className="text-xs">
                                      {castType}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No operators available</p>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 mb-6">
              <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-successColor to-green-600 flex items-center justify-center shadow-md">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">Common Parameters</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {commonParameters && commonParameters.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Parameter Path</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Label</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Type</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Description</th>
                          <th className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Example Value</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {commonParameters.map((parameter: any) => (
                          <tr key={parameter.path} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-4 whitespace-nowrap">
                              <code className="text-sm font-semibold text-primaryColor bg-primaryColor/10 px-2.5 py-1 rounded">
                                {parameter.path}
                              </code>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-700 font-medium">{parameter.label || '—'}</td>
                            <td className="px-5 py-4 text-sm text-gray-700">
                              <Badge variant="secondary" className="font-semibold">
                                {parameter.castType || 'string'}
                              </Badge>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-700">{parameter.description || '—'}</td>
                            <td className="px-5 py-4 text-sm text-gray-600 font-mono bg-gray-50 rounded px-3 py-1.5">
                              {parameter.exampleValue || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No parameters available</p>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-md">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M16 13H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M16 17H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 9H9H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">Example Rules</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {examples && examples.length > 0 ? (
                  <div className="space-y-5">
                    {examples.map((example: any, index: number) => (
                      <div
                        key={index}
                        className="border-2 border-gray-200 rounded-xl p-5 bg-gradient-to-br from-white to-gray-50 hover:border-primaryColor/50 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-primaryColor/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primaryColor font-bold text-sm">{index + 1}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-lg text-gray-900 mb-1">{example.title}</h4>
                            <p className="text-sm text-gray-600">{example.description}</p>
                          </div>
                        </div>
                        {example.ruleConfig && (
                          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 overflow-x-auto">
                            <pre className="text-sm text-gray-100 font-mono overflow-x-auto">
                              {JSON.stringify(example.ruleConfig, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No examples available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instructions;
